import { z } from "zod";
import { Meter } from "../../shared/pricing";
import { AiVendor } from "../../shared/vendors";

export const ModelName = z.string().min(1).meta({
  id: "ModelName",
  description:
    "A model's name as the seller spells it: a direct vendor's own name, or an OpenRouter slug such as `openai/gpt-6-luna`.",
  example: "claude-sonnet-5-5",
});

export const ModelParams = z.strictObject({ model: ModelName });
export type ModelParams = z.infer<typeof ModelParams>;

export const SkuId = z.string().meta({
  id: "SkuId",
  description: "`<billedBy>:<model>:<endpoint>:<tier>`.",
  example: "anthropic:claude-sonnet-5-5:global:standard",
});

export const PriceRow = z
  .object({
    id: z.string().meta({ description: "Unique within its SKU; a cost line names `<sku>#<id>`." }),
    meter: Meter,
    unit: z.enum(["1M tokens", "1k uses", "request"]),
    currency: z.literal("USD"),
    amount: z.number().nonnegative(),
    effective: z
      .object({ from: z.string().optional(), until: z.string().optional() })
      .optional()
      .meta({
        description: "UTC dates, `YYYY-MM-DD`: the first day it holds and the first it doesn't.",
      }),
    window: z
      .object({ from: z.string(), to: z.string() })
      .optional()
      .meta({ description: "UTC time of day, `HH:MM`, by when Pax received the call." }),
    promptTokens: z
      .object({ over: z.number().int().optional(), upTo: z.number().int().optional() })
      .optional()
      .meta({
        description:
          "The prompt's size band: `over` excludes its value, `upTo` includes it. The whole call is priced at its band.",
      }),
  })
  .meta({
    id: "PriceRow",
    description:
      "One price: an amount per unit of one meter, under the conditions given. An absent condition holds for every call.",
  });
export type PriceRow = z.infer<typeof PriceRow>;

export const Sku = z
  .object({
    id: SkuId,
    billedBy: AiVendor,
    model: ModelName,
    endpoint: z.string().meta({
      description:
        "Where calls go and are billed from, such as `virginia`, `us` or `amazon-bedrock/us`.",
    }),
    tier: z.string().meta({ description: "The service tier as the seller reports it." }),
    promptTokensCount: z.literal("allInput").meta({
      description:
        "Which tokens count toward `promptTokens` bands: every input token, cached or not.",
    }),
    maxOutputTokens: z.number().int().positive().optional(),
    prices: z.array(PriceRow).readonly(),
  })
  .meta({
    id: "Sku",
    description:
      "Something Pax can be billed for: one model on one endpoint at one tier, billed by one seller, with its flat list of prices.",
  });
export type Sku = z.infer<typeof Sku>;

export const Model = z
  .object({
    model: ModelName,
    billedBy: AiVendor,
    skus: z
      .array(z.object({ id: SkuId, endpoint: z.string(), tier: z.string() }))
      .meta({ description: "Every endpoint and tier the seller bills the model at." }),
  })
  .meta({
    id: "Model",
    description: "A model Pax prices, the seller who bills it, and its SKUs.",
    example: {
      model: "qwen3.8-max",
      billedBy: "alibaba",
      skus: [
        { id: "alibaba:qwen3.8-max:virginia:standard", endpoint: "virginia", tier: "standard" },
      ],
    },
  });
export type Model = z.infer<typeof Model>;

export const ListModelsResponse = z.object({ items: z.array(Model) }).meta({
  id: "ListModelsResponse",
  description: "Every model Pax prices, once per seller that bills it.",
});
export type ListModelsResponse = z.infer<typeof ListModelsResponse>;

export const ModelPricesResponse = z
  .object({
    model: ModelName,
    skus: z
      .array(Sku)
      .meta({ description: "Every SKU of the model, from every seller that bills it." }),
  })
  .meta({
    id: "ModelPricesResponse",
    description: "A model's SKUs and their prices.",
    example: {
      model: "qwen3.8-max",
      skus: [
        {
          id: "alibaba:qwen3.8-max:virginia:standard",
          billedBy: "alibaba",
          model: "qwen3.8-max",
          endpoint: "virginia",
          tier: "standard",
          promptTokensCount: "allInput",
          prices: [
            {
              id: "uncachedInput",
              meter: "uncachedInput",
              unit: "1M tokens",
              currency: "USD",
              amount: 1.65,
            },
            { id: "output", meter: "output", unit: "1M tokens", currency: "USD", amount: 4.951 },
          ],
        },
      ],
    },
  });
export type ModelPricesResponse = z.infer<typeof ModelPricesResponse>;
